import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  MailLabelFieldsFragment,
  MailMailboxFieldsFragment,
  MailScheduledListQuery,
  MailSignaturesListDocument,
  useImportMailboxSignaturesMutation,
  useMailAiStatusCheckQuery,
  useMailLabelsListQuery,
  useMailMailboxesListQuery,
  useMailScheduledListQuery,
  useStartMailHistoryLoadMutation,
} from 'core/graphql';

import {
  IMailHistoryForm,
  mailHistoryRunning,
  mailHistoryVariables,
} from './mailHistoryModel';
import { IMailResult, mailFailure } from './mailModel';

export type IMailbox = MailMailboxFieldsFragment;
export type IMailLabel = MailLabelFieldsFragment;

const NO_BOXES: IMailbox[] = [];
const MAIL_HISTORY_POLL_MS = 10000;
const NO_LABELS: IMailLabel[] = [];

/** ТЗ 0122 — ящики сотрудника со счётчиками (Admin — все включённые). */
export const useMailboxes = (skip = false) => {
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useMailMailboxesListQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const mailboxes = data?.mailMailboxes ?? NO_BOXES;
  // Догрузка истории идёт — счётчик «+N писем» и конец догрузки видны без
  // перезагрузки: ревизия почты меняется не на каждый шаг догрузки.
  const historyRunning = mailHistoryRunning(mailboxes);
  useEffect(() => {
    if (skip || !historyRunning) return undefined;
    startPolling(MAIL_HISTORY_POLL_MS);
    return () => stopPolling();
  }, [skip, historyRunning, startPolling, stopPolling]);
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    mailboxes,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

export interface IMailHistoryResult extends IMailResult {
  /** Сколько ящиков поставлено в догрузку. */
  count: number;
}

/**
 * ТЗ 0122 — «Загрузить историю» (только Admin): догрузка старых писем в
 * фоне. Форма не заполнена — `INVALID` без запроса.
 */
export const useMailHistoryLoad = () => {
  const [run] = useStartMailHistoryLoadMutation();
  const [busy, setBusy] = useState(false);
  const start = useCallback(
    async (form: IMailHistoryForm): Promise<IMailHistoryResult> => {
      const variables = mailHistoryVariables(form);
      if (!variables) return { ok: false, errorCode: 'INVALID', count: 0 };
      setBusy(true);
      try {
        const { data } = await run({ variables });
        const payload = data?.mailLoadHistory;
        if (!payload) return { ok: false, errorCode: 'UNAVAILABLE', count: 0 };
        return {
          ok: payload.ok,
          error: payload.error,
          errorCode: payload.errorCode,
          count: payload.count || 0,
        };
      } catch (caught) {
        return { ...mailFailure(caught), count: 0 };
      } finally {
        setBusy(false);
      }
    },
    [run],
  );
  return { start, busy };
};

/** Метки выбранных ящиков (`null` — всех моих). */
export const useMailLabels = (mailboxIds: string[] | null, skip = false) => {
  const { data, loading, refetch } = useMailLabelsListQuery({
    variables: { mailboxIds },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const labels = data?.mailLabels ?? NO_LABELS;
  const userLabels = useMemo(
    () => labels.filter((label) => label.type === 'user'),
    [labels],
  );
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return { labels, userLabels, loading: loading && !data, reload };
};

/** Показывать ли кнопки ИИ (флаг сервера). */
export const useMailAiStatus = (skip = false) => {
  const { data } = useMailAiStatusCheckQuery({
    skip,
    fetchPolicy: 'cache-first',
  });
  return {
    enabled: Boolean(data?.mailAiStatus.enabled),
    autoTriage: Boolean(data?.mailAiStatus.autoTriage),
    autoDraft: Boolean(data?.mailAiStatus.autoDraft),
    /** Ответ пришёл — «выключено» уже не догадка. */
    loaded: Boolean(data),
  };
};

/**
 * Подписи из Gmail (`sendAs`) — один раз за сессию вкладки при первом
 * открытии почты; дальше подписи читает `useMailSignatures` (ТЗ 0097).
 * Ошибка (нет скоупа `gmail.settings.basic`) не мешает работе — подписи
 * останутся ручными.
 */
let signaturesImported = false;

export const useImportMailSignaturesOnce = (enabled: boolean) => {
  const [run] = useImportMailboxSignaturesMutation();
  useEffect(() => {
    if (!enabled || signaturesImported) return;
    signaturesImported = true;
    run({ refetchQueries: [{ query: MailSignaturesListDocument }] }).catch(
      () => undefined,
    );
  }, [enabled, run]);
};

export type IMailScheduled = MailScheduledListQuery['mailScheduled'][number];

const NO_SCHEDULED: IMailScheduled[] = [];

/** Запланированные письма (ждут, отправляются, не ушли). */
export const useMailScheduled = (mailboxIds: string[] | null, skip = false) => {
  const { data, loading, refetch } = useMailScheduledListQuery({
    variables: { mailboxIds },
    skip,
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    if (skip) return;
    refetch().catch(() => undefined);
  }, [skip, refetch]);
  return {
    scheduled: data?.mailScheduled ?? NO_SCHEDULED,
    loading: loading && !data,
    reload,
  };
};
